import React, { useState, useEffect, useRef, useCallback } from 'react';
import Highlighter from 'react-highlight-words';
import debounce from 'lodash/debounce';
import AutoComplete from 'antd/es/auto-complete';
import Select from 'antd/es/select';
import { useSelector, useDispatch } from 'react-redux';

import { FormattedMessage } from 'react-intl';

import { IActivityLocationsState, getActivityLocations, activityLocationActions, ILoginState, activityFiltersActions } from '@share/store/slices';


import { ALL_INCLUSIVE_VALUE, ENTER_KEY, ACTIVITY_RECENT_SEARCHES_LABEL } from '@share/constants';
import { getRecentSearchLocationKeyHotel, RootState, AppDispatch } from '@share/utils';
import { LocationsTypes, ILocation } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCampground, faLocationDot, faPlane, faShip } from '@fortawesome/free-solid-svg-icons';

// All-inclusive destinations modal is unchanged from RSI's.
import { ModalAllInclusiveDestinations } from '../../search/modal-all-inclusive-destinations';

import './style.scss';

const zero = 0;
const MIN_SEARCH_LENGTH = 3;
const LIST_HEIGHT = 500;
// Minimum usable list height, and vertical space the dropdown needs below the
// input (its `margin-top` plus a small gap) so it can be capped to the viewport.
const MIN_LIST_HEIGHT = 120;
const DROPDOWN_TOP_OFFSET = 54;
const DROPDOWN_BOTTOM_GAP = 24;
const SEARCH_DEBOUNCE = 500;
const { Option, OptGroup } = Select;

export const ActivityLocationsPicker: React.FC<any> = ({
  isExperiences,
  disable,
  openOnClick
}) => {

  const dispatch = useDispatch<AppDispatch>();

  const activityLocationsStore: IActivityLocationsState = useSelector((state: RootState) => state.activityLocationsStore);
  const loginStore: ILoginState = useSelector((state: RootState) => state.loginStore);

  const {
    selectedLocationLabel,
    selectedLocation,
    locations,
    loading,
    error
  } = activityLocationsStore;

  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [displayAllDestinations, setDisplayAllDestinations] = useState(false);
  const [listHeight, setListHeight] = useState(LIST_HEIGHT);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);


  const lastSearchRef = useRef('');
  const lastSelectedLocationRef = useRef<ILocation | null | undefined>(null);

  const setLocations = (v: ILocation[]) => dispatch(activityLocationActions.setLocations(v));
  const setSelectLocation = (v: ILocation | undefined) => dispatch(activityLocationActions.setSelectLocation(v));
  const setSelectLocationLabel = (v: string) => dispatch(activityLocationActions.setSelectLocationLabel(v));
  const setLoading = () => dispatch(activityLocationActions.setLoading(true));
  const clearError = () => dispatch(activityLocationActions.setError(''));
  const setBoardName = (v: string[]) => dispatch(activityFiltersActions.setBoardName(v));

  const onFocus = () => {
    if (!openOnClick) {
      if (!isFocused) {
        onSearch(selectedLocationLabel || '');
      }
      setIsFocused(true);
      setIsDropdownOpen(true);
    }
  };

  const onChange = (value: string, option: any) => {
    const location = locations.find(({ code }) => code?.toString() === (option?.key as number)?.toString());

    if (openOnClick && !displayAllDestinations) {
      setDisplayAllDestinations(true);
      setIsFocused(false);
      return;
    }

    if (!value) {
      setIsDropdownOpen(!openOnClick);
      setIsFocused(true);
    } else {
      setIsDropdownOpen(false);
      setIsFocused(true);
    }

    setSelectLocationLabel(value);
    setSelectLocation(location);
    setLocations([]);

    lastSelectedLocationRef.current = location;

    if (option?.key) clearError();
  };

  const handleSelectDestination = (location: ILocation) => {
    setIsDropdownOpen(false);
    setIsFocused(true);

    if (!isExperiences) {
      setSelectLocationLabel(location.name);
      setSelectLocation(location);
      setBoardName([ALL_INCLUSIVE_VALUE]);
    }

    setLocations([]);
    clearError();
    lastSelectedLocationRef.current = location;
  };

  // --- Debounced Search ---
  const debouncedSearch = useCallback(
    debounce((search: string) => {
      const onlyCities = isExperiences || false
      dispatch(getActivityLocations(search, selectedLocation, onlyCities));
    }, SEARCH_DEBOUNCE),
    [selectedLocation, isExperiences]
  );

  // --- On Search ---
  const onSearch = (search: string) => {
    lastSearchRef.current = search;

    const stored = localStorage.getItem(ACTIVITY_RECENT_SEARCHES_LABEL);
    const list = stored ? JSON.parse(stored) : [];
    let recentLocations: ILocation[] = [];

    if (list?.length) {
      const seenKeys = new Set<string>();
      const dedupedStored = list.filter((x: any) => {
        const key = getRecentSearchLocationKeyHotel(x);
        if (!key || seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });
      recentLocations = dedupedStored
        .map((x: any) => ({
          ...x.location,
          realType: x.location.realType || x.location.type,
          type: LocationsTypes.Recents,
        }))
        .slice(0, 5);
    }

    setIsDropdownOpen(!openOnClick || !!search || (search === '' && !!recentLocations.length));

    if (search.length >= MIN_SEARCH_LENGTH) {
      setLoading();
      debouncedSearch(search);
    } else {
      setLocations([...recentLocations]);
    }
  };

  // --- Focus programmatically ---
  const focusSelect = () => {
    if (selectRef.current && !displayAllDestinations) {
      selectRef.current.focus();
    }
  };

  // --- Group Locations ---
  const getGroupedLocation = () => {
    const destinations: ILocation[] = [];
    const activities: ILocation[] = [];
    const hotels: ILocation[] = [];
    const airports: ILocation[] = [];
    const ports: ILocation[] = [];
    const recents: ILocation[] = [];

    locations.forEach((loc) => {
      switch (loc.type) {
        case LocationsTypes.Airport:
          airports.push(loc);
          break;
        case LocationsTypes.Activity:
          activities.push(loc);
          break;
        case LocationsTypes.Destination:
          destinations.push(loc);
          break;
        case LocationsTypes.Hotel:
          hotels.push(loc);
          break;
        case LocationsTypes.Port:
          ports.push(loc);
          break;
        case LocationsTypes.Recents:
          if (
            !isExperiences ||
            (loc.realType &&
              [LocationsTypes.Destination, LocationsTypes.Recents].includes(loc.realType))
          ) {
            recents.push({ ...loc, type: loc.realType });
          }
          break;
        default:
          break;
      }
    });

    return { destinations, activities, hotels, airports, ports, recents };
  };

  const grouped = getGroupedLocation();

  const getOptionGroup = (
    array: ILocation[],
    label: string,
    icon: React.ReactNode
  ) => {
    if (!array.length) {
      return null;
    }

    return (
      <OptGroup label={<FormattedMessage id={label} />}>
        {array.map((location: ILocation) => {
          const inner = (
            <>
              <span className="activity-locations-dropdown__option-icon">{icon}</span>
              <span className="activity-locations-dropdown__location-name">
                <Highlighter
                  highlightClassName="bold-text"
                  searchWords={[lastSearchRef.current.trim()]}
                  autoEscape
                  textToHighlight={location.name}
                />
              </span>
            </>
          );

          return (
            <Option
              key={location.code}
              value={location.name}
              className={`activity-locations-dropdown__option ${location.type === LocationsTypes.Hotel ? 'hotel' : ''
                }`}
            >
              <div className="activity-locations-dropdown__option-wrapper">{inner}</div>
            </Option>
          );
        })}
      </OptGroup>
    );
  };

  // --- Outside click handler ---
  const handleClickOutside = (e: MouseEvent) => {
    if (!openOnClick) {
      if (
        !wrapperRef.current?.contains(e.target as Node) ||
        (e.target as HTMLElement).classList.contains('activity-locations-picker__fixed-bg')
      ) {
        setIsFocused(false);
        setIsDropdownOpen(false);
        setLocations([]);
        lastSearchRef.current = '';
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Cap the list height to the space available below the input ---
  // The dropdown is hard-positioned right under the input, so on short viewports
  // it would otherwise spill off-screen and show nothing. Constrain it to the
  // remaining space so the list stays visible and scrolls internally.
  const computeListHeight = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const available =
      window.innerHeight - rect.top - DROPDOWN_TOP_OFFSET - DROPDOWN_BOTTOM_GAP;

    setListHeight(Math.max(MIN_LIST_HEIGHT, Math.min(LIST_HEIGHT, available)));
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;

    computeListHeight();
    window.addEventListener('resize', computeListHeight);
    return () => window.removeEventListener('resize', computeListHeight);
  }, [isDropdownOpen, computeListHeight]);


  const getPopupContainer = () => wrapperRef.current || document.body;


  const getNotFoundContent = () => {
    let message;

    if (selectedLocationLabel?.length < MIN_SEARCH_LENGTH)
      message = <FormattedMessage id="search.min.symbols" />;
    else if (loading)
      message = <FormattedMessage id="searching" />;
    else
      message = <FormattedMessage id="nothing.found" />;

    return <div className="activity-locations-dropdown__nothing-found">{message}</div>;
  };

  // --- Click handler ---
  const onClick = (event: any) => {
    const target = event.target as HTMLElement;

    if (openOnClick) {
      setDisplayAllDestinations(true);
      return;
    }

    if (
      selectedLocationLabel &&
      isFocused &&
      !isDropdownOpen &&
      target.classList.contains('ant-select-selection-search-input')
    ) {
      onSearch(selectedLocationLabel);
    } else if (
      target.classList.contains('ant-select-item-option-selected') ||
      target.closest('.ant-select-item-option-selected') ||
      target.closest('.ant-select-item-option-active')
    ) {
      if (lastSelectedLocationRef.current) {
        setIsDropdownOpen(false);
        setIsFocused(true);
        setLocations([]);
      } else {
        const location = locations.find(
          ({ name }) => name.toLowerCase() === selectedLocationLabel.toLowerCase()
        );
        if (location) {
          setIsDropdownOpen(false);
          setIsFocused(true);
          setSelectLocationLabel(location.name);
          setSelectLocation(location);
          setLocations([]);
          lastSelectedLocationRef.current = location;
          clearError();
        }
      }
    } else if (selectedLocation) {
      setIsDropdownOpen(isFocused || false);
      setIsFocused(true);
      clearError();
    }
  };

  // --- Enter key select ---
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.code === ENTER_KEY &&
      isDropdownOpen &&
      isFocused &&
      locations.length &&
      !lastSelectedLocationRef.current
    ) {
      const item = locations[zero];

      setIsDropdownOpen(false);
      setIsFocused(true);
      setSelectLocationLabel(item.name);
      setSelectLocation(item);
      setLocations([]);
      lastSelectedLocationRef.current = item;
      clearError();
    }
  };

  // --- UI ---

  const account = loginStore?.account;
  const color = account?.searchIconsColor;
  const styleColor = color ? { color } : {};

  const generalBorderRadius = account?.generalBorderRadius || null;
  const styles = generalBorderRadius
    ? { borderRadius: `${generalBorderRadius}px` }
    : {};

  return (
    <div className={`activity-locations-picker ${error ? 'error' : ''}`} ref={wrapperRef}>
      <div
        className={`activity-locations-picker__wrapper ${isFocused ? 'selected' : ''}`}
        onClick={focusSelect}
        style={styles}
      >
        <div className="activity-locations-picker__left">
          <div className="activity-locations-picker__icon" style={styleColor}>
            <FontAwesomeIcon icon={faLocationDot} />
          </div>
        </div>

        <div className="activity-locations-picker__right">
          <div className="activity-locations-picker__label">
            <FormattedMessage id="location" />
          </div>

          <div className={`activity-locations-picker__input ${openOnClick ? 'open-click' : ''}`}>
            <AutoComplete
              onKeyDown={onKeyDown}
              virtual={false}
              showSearch={true}
              placeholder={<FormattedMessage id="location.input.placeholder" />}
              onChange={onChange}
              onSearch={onSearch}
              onFocus={onFocus}
              suffixIcon={null}
              ref={selectRef}
              open={isDropdownOpen}
              defaultActiveFirstOption={false}
              filterOption={() => true}
              autoClearSearchValue={false}
              getPopupContainer={getPopupContainer as any}
              listHeight={listHeight}
              allowClear={!openOnClick}
              value={selectedLocationLabel}
              notFoundContent={getNotFoundContent()}
              onClick={onClick}
              disabled={disable}
              key={activityLocationsStore.locationKey}
            >
              {selectedLocationLabel?.length < MIN_SEARCH_LENGTH && (
                <Option key={MIN_SEARCH_LENGTH} value={MIN_SEARCH_LENGTH} disabled className="activity-locations-dropdown__option">
                  <FormattedMessage id="search.min.symbols" />
                </Option>
              )}

              {getOptionGroup(grouped.destinations, 'destinations', <FontAwesomeIcon icon={faLocationDot} style={{ color: '#D2D2D2' }} />)}
              {getOptionGroup(grouped.activities, 'activities', <FontAwesomeIcon icon={faCampground} style={{ color: '#D2D2D2' }} />)}
              {getOptionGroup(grouped.hotels, 'hotels', <FontAwesomeIcon icon={faBuilding} style={{ color: '#D2D2D2' }} />)}
              {getOptionGroup(grouped.airports, 'airports', <FontAwesomeIcon icon={faPlane} style={{ color: '#D2D2D2' }} />)}
              {getOptionGroup(grouped.ports, 'ports', <FontAwesomeIcon icon={faShip} style={{ color: '#D2D2D2' }} />)}
              {getOptionGroup(grouped.recents, 'recents', <FontAwesomeIcon icon={faLocationDot} style={{ color: '#D2D2D2' }} />)}
            </AutoComplete>
          </div>
        </div>
      </div>

      <ModalAllInclusiveDestinations
        visible={displayAllDestinations}
        onSelect={handleSelectDestination}
        onCancel={() => {
          setDisplayAllDestinations(false);
          setIsFocused(false);
          selectRef.current?.blur();
        }}
      />

      {isDropdownOpen && <div className="activity-locations-picker__fixed-bg" />}
      {/* {error && <div className="activity-locations-picker__error">{error}</div>} */}
    </div>
  );
};
